import { useState } from "react";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/customTable.scss";
import useColumnResize from "../../hooks/useColumnResize";

const DataImportTable = ({ data, onSortChange, onSelectionChange, selectedIds = [] }) => {
const [sortConfig, setSortConfig] = useState({ key: "StartDate", direction: "desc" });
const navigate = useNavigate();
const { getResizeProps } = useColumnResize();

  const items = Array.isArray(data) ? data : [];

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    onSortChange(newSortConfig);
  };

  const getSortArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "↑" : "↓";
    }
    return "";
  };

  const handleRowClick = (item) => {
    navigate(`/data-import/${item.id}`);
  };

  // Zaznaczyc mozna wylacznie wpisy w odpowiednim statusie (checkbox) ORAZ takie,
  // ktore uzytkownik moze modyfikowac (canModify z backendu). Zwykly uzytkownik widzi
  // wpisy calego oddzialu, ale odrzucac moze tylko wlasne — bez tego warunku
  // zaznaczylby wpis kolegi, a akcja zwrocilaby blad uprawnien.
  const isSelectable = (item) => item.checkbox && item.canModify !== false;

  const selectableIds = items.filter(isSelectable).map((item) => item.id);
  const selectAll = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      // Zaznacz tylko te, które mają checkbox === true
      const newSelectedIds = [...new Set([...selectedIds, ...selectableIds])];
      onSelectionChange?.(newSelectedIds);
    } else {
      const newSelectedIds = selectedIds.filter((id) => !selectableIds.includes(id));
      onSelectionChange?.(newSelectedIds);
    }
  };

  const handleCheckboxChange = (e, itemId) => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    let newSelectedIds;

    if (isChecked) {
      newSelectedIds = [...selectedIds, itemId];
    } else {
      newSelectedIds = selectedIds.filter((id) => id !== itemId);
    }

    onSelectionChange?.(newSelectedIds);
  };

  // Parsuje JSON string z danych/filtrów do czytelnego formatu
  const formatJsonData = (jsonStr) => {
    if (!jsonStr) return "";
    try {
      const parsed = JSON.parse(jsonStr);

      // Format w stylu (nazwa_pola: ilość_zmian)
      if (parsed.columns && typeof parsed.columns === "object") {
        const entries = Object.entries(parsed.columns);
        // totalChanges = zawsze suma wartości columns
        const totalChanges = entries.reduce((s, [, v]) => s + v, 0);

        // Budowanie listy szczegółów: dla Masowej aktualizacji z values — pokaż wartości pól
        const hasValues = parsed.values && typeof parsed.values === "object";

        // Rozkład nowych wartości (import CSV) — może nie istnieć dla starszych rekordów
        const valueCounts = parsed.valueCounts;

        // Skrócony opis "na co zmieniamy" — na liście pokazujemy tylko kilka pierwszych wartości,
        // pełny rozkład jest dostępny na stronie szczegółów importu.
        const formatTopValues = (col) => {
          const dist = valueCounts?.[col];
          if (!dist?.topValues) return "";

          const items = Object.entries(dist.topValues);
          if (items.length === 0) return "";

          const preview = items
            .slice(0, 3)
            .map(([val, cnt]) => `${val === "" ? "(puste)" : val}: ${cnt}`)
            .join(", ");

          const remaining = items.length - 3 + (dist.otherValuesCount || 0);

          return remaining > 0 ? ` (→ ${preview}, +${remaining}…)` : ` (→ ${preview})`;
        };

        return (
          <details style={{ display: "inline" }}>
            <summary style={{ cursor: "pointer", display: "inline", color: "#0d6efd", userSelect: "none" }}>
                    Zmian: {totalChanges} <br/> kliknij, aby rozwinąć
            </summary><br/>
            <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px", fontSize: "0.85em" }}>
              {entries.map(([k, v]) => (
                <div key={k}>
                  <strong>{k}</strong>: {v}
                  {hasValues && parsed.values[k] !== undefined ? ` (→ ${parsed.values[k]})` : formatTopValues(k)}
                </div>
              ))}
            </ul>
          </details>
        );
      }

      // Format domyślny
      const fallbackEntries = Object.entries(parsed);
      if (fallbackEntries.length === 0) return "";

      return (
        <details style={{ display: "inline" }}>
          <summary style={{ cursor: "pointer", display: "inline", color: "#0d6efd", userSelect: "none" }}>
                  Zmian: {fallbackEntries.length} <br/> kliknij, aby rozwinąć
          </summary><br/>
          <ul style={{ margin: "4px 0 0 0", paddingLeft: "16px", fontSize: "0.85em" }}>
            {fallbackEntries.map(([key, value]) => (
              <div key={key}>
                <strong>{key}</strong>: {value && typeof value === "object" ? JSON.stringify(value) : String(value)}
              </div>
            ))}
          </ul>
        </details>
      );
    } catch {
      return jsonStr;
    }
  };

  // Styl dla wierszy z zbyt długimi wartościami na kolumnie
  const ellipsisStyle = (maxWidth) => ({
    maxWidth,
    minWidth: "150px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  });

  return (
    <div>
      <Table responsive striped bordered hover size="sm" className="custom-table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>
              <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
            </th>
            <th onClick={() => requestSort("StartDate")} style={{ minWidth: "95px" }} {...getResizeProps()}>
              Data rozpoczęcia {getSortArrow("StartDate")}
            </th>
            <th onClick={() => requestSort("ExecutionDate")} style={{ minWidth: "95px" }} {...getResizeProps()}>
              Data wykonania {getSortArrow("ExecutionDate")}
            </th>
            <th onClick={() => requestSort("RollbackDate")} style={{ minWidth: "95px" }} {...getResizeProps()}>
              Data wycofania {getSortArrow("RollbackDate")}
            </th>
            <th onClick={() => requestSort("UserName")} style={{ minWidth: "95px" }} {...getResizeProps()}>
              Dodał {getSortArrow("UserName")}
            </th>
            <th onClick={() => requestSort("AcceptUserName")} {...getResizeProps()}>
              Zaakceptował {getSortArrow("AcceptUserName")}
            </th>
            <th onClick={() => requestSort("Status")} {...getResizeProps()}>
              Status {getSortArrow("Status")}
            </th>
            <th onClick={() => requestSort("Action")} style={{ minWidth: "95px" }} {...getResizeProps()}>
              Rodzaj {getSortArrow("Action")}
            </th>
            <th onClick={() => requestSort("EndDate")} style={{ minWidth: "95px" }} {...getResizeProps()}>
              Data zakończenia {getSortArrow("EndDate")}
            </th>
            <th onClick={() => requestSort("CountTotal")} {...getResizeProps()}>
              Rekordów {getSortArrow("CountTotal")}
            </th>
            <th onClick={() => requestSort("CountDone")} {...getResizeProps()}>
              Zmodyfikowanych {getSortArrow("CountDone")}
            </th>
            <th onClick={() => requestSort("CountSkipped")} {...getResizeProps()}>
              Bez zmian {getSortArrow("CountSkipped")}
            </th>
            <th onClick={() => requestSort("CountLeft")} {...getResizeProps()}>
              Pozostało {getSortArrow("CountLeft")}
            </th>
            <th onClick={() => requestSort("FileName")} {...getResizeProps()}>
              Plik {getSortArrow("FileName")}
            </th>
            <th {...getResizeProps()}>Wartości</th>
            <th {...getResizeProps()}>Filtr</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item, index) => (
              <tr
                key={`${item.id}-${index}`}
                onClick={() => handleRowClick(item)}
                style={{
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.closest("tr").style.backgroundColor = "#f8f9fa")}
                onMouseLeave={(e) => (e.target.closest("tr").style.backgroundColor = "")}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={(e) => handleCheckboxChange(e, item.id)}
                    disabled={!isSelectable(item)}
                    title={
                      item.checkbox && item.canModify === false
                        ? "Import utworzony przez innego użytkownika — brak uprawnień do modyfikacji"
                        : undefined
                    }
                  />
                </td>
                <td>{item.dateStart}</td>
                <td>{item.executionDate}</td>
                <td>{item.rollbackDate}</td>
                <td>{item.userName}</td>
                <td>{item.acceptUserName}</td>
                <td>
                  <span className={`badge bg-${getStatusBadgeColor(item.status)}`}>
                    {item.statusName}
                  </span>
                </td>
                <td>{item.actionName}</td>
                <td>{item.dateEnd}</td>
                <td>{item.countTotal}</td>
                <td>{item.countDone}</td>
                <td>{item.countSkipped}</td>
                <td>{item.countLeft}</td>
                <td style={ellipsisStyle("350px")} title={item.fileName}>
                  {item.fileName}
                </td>
                <td style={{ maxWidth: "400px", minWidth: "150px", whiteSpace: "normal" }} onClick={(e) => e.stopPropagation()}>
                  {formatJsonData(item.data)}
                </td>
                <td style={{ maxWidth: "400px", minWidth: "150px", whiteSpace: "normal" }} onClick={(e) => e.stopPropagation()}>
                  {formatJsonData(item.filter)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="16" className="text-center">
                Brak danych
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

// Kolory badge'ów statusów
const getStatusBadgeColor = (status) => {
  switch (status) {
    case -1:
      return "warning";
    case 0:
      return "info";
    case 1:
      return "primary";
    case 2:
      return "success";
    case 4:
      return "danger";
    case 5:
      return "secondary";
    case 6:
      return "info";
    case 7:
      return "dark";
    case 8:
      return "primary";
    default:
      return "light";
  }
};

export default DataImportTable;
